import {Check} from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import SensorsIcon from '@mui/icons-material/Sensors'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import {useState} from 'react'

import {useUrlParam} from '../../../../../shared/hooks/useUrlParam'
import {useStoreUserValue} from '../../../../shared/hooks/userStorage/storeValue'
import {SignalsListType, UserSignalGroup} from '../../../../shared/types/sensor.types'
import {usePlantMonitoringSelectors} from '../../../context/PlantMonitoringContext'
import {useSignalMetadata} from '../../../context/SignalMetadataContext'
import {useGetGroupsData} from '../../../hooks/useGetGroupsData'

export const AddGroupModalGraphs = ({
  isOpen,
  handleClose
}: {
  isOpen: boolean
  handleClose: () => void
}) => {
  const {mutateAsync: saveGroup} = useStoreUserValue()
  const {selectedIds, groupDataFromState} = usePlantMonitoringSelectors()
  const {signals} = useSignalMetadata()
  const {userGroups, isPending} = useGetGroupsData()

  const [groupName, setGroupName] = useState<string>()
  const [isError, setIsError] = useState<string>()
  const [selectedSignals, setSelectedSignals] = useState<SignalsListType[]>(
    signals.filter((signal) => selectedIds.includes(signal.localName))
  )
  const plantId = useUrlParam('plantId')

  const submitGroups = async () => {
    if (!groupName || groupName === '') {
      setIsError('Group name is required')
      return
    }

    const duplicate = userGroups.find((g) => g.name === groupName)

    if (duplicate) {
      setIsError('Group name is duplicate please select another name')
      return
    }

    if (selectedSignals.length > 0) {
      const selectedSignalIds = selectedSignals.map((s) => s.localName)
      const newGroup: UserSignalGroup = {
        name: groupName,
        signals: selectedSignalIds,
        plantId: plantId,
        ...groupDataFromState,
        chartsOrder: groupDataFromState.chartsOrder.filter(
          (chartId) => selectedSignalIds.includes(chartId) || chartId.startsWith('combined')
        ),
        masterUpmId: null // TODO: here we will potentially ad upm id of the Kiln etc. but currently new groups can be added only in plant monitoring
      }
      const newGroups = [...userGroups.filter((group) => group.type === 'user'), newGroup]
      await saveGroup({
        keyIdentifier: 'pm-user-groups',
        value: JSON.stringify(newGroups)
      })
    }
    handleClose()
  }

  const handleDelete = (localName: string) => {
    setSelectedSignals(selectedSignals.filter((s) => s.localName !== localName))
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} aria-labelledby="add-group-modal" fullWidth>
      <DialogTitle id="add-group-modal-title" variant="h4">
        New group
      </DialogTitle>
      <DialogContent sx={{overflowY: 'hidden'}}>
        <TextField
          label={'Group name'}
          fullWidth
          error={isError !== undefined}
          helperText={isError}
          sx={{marginTop: 2}}
          onChange={(e) => {
            setGroupName(e.target.value)
            setIsError(undefined)
          }}
        />
        <Box mb={2} mt={2}>
          <Divider />
        </Box>
        <Box>
          {selectedSignals.map((signal) => (
            <Chip
              icon={<SensorsIcon />}
              key={signal.localName}
              sx={{mb: 1}}
              label={`${signal.equipment.name} > ${signal.description}`}
              deleteIcon={<CloseIcon />}
              onDelete={() => handleDelete(signal.localName)}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{backgroundColor: '#FFF'}}
          width={'100%'}
        >
          <Typography variant={'h3'}>{`${selectedIds.length} sensors selected`}</Typography>
          <Stack gap={2} direction={'row'}>
            <Button
              onClick={handleClose}
              variant={'outlined'}
              disabled={isPending}
              startIcon={<CloseIcon />}
            >
              Cancel
            </Button>
            <Button
              onClick={submitGroups}
              variant={'contained'}
              disabled={isPending}
              startIcon={<Check />}
            >
              Save new Group
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
